import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Formik, Form } from "formik"
import { Typography, Button, FormControl, TextField, InputLabel, Select, MenuItem } from "@mui/material"
import * as Yup from "yup"
import { makeStyles } from "@mui/styles"
import { DatePicker } from "@mui/x-date-pickers"
import moment from "moment"

import { getProductionStepsSitesSelector } from "../../reducers/Production/ProductionSteps"
import { updateProductionStepsFilters, showProductionStepsProdPlanner } from "../../actions/ProductionSteps/ProductionStepExecutions"
import { COLORS } from "../../utils"

const schema = Yup.object().shape({
	kitchenArea: Yup.string().required("Veuillez sélectionner un atelier"),
	productionDay: Yup.string().required("Veuillez sélectionner un jour de production")
})

const ProdPlannerFilter = ({ selectedDate, setSelectedDate, selectedKitchenArea, setSelectedKitchenArea }) => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const sites = useSelector(getProductionStepsSitesSelector)
	const [kitchenAreasOptions, setKitchenAreasOptions] = useState([])

	useEffect(() => {
		const updatedKitchenAreas = sites.reduce((acc, site) => { return [...acc, ...site.kitchenAreas]}, [])
		setKitchenAreasOptions(updatedKitchenAreas)
	}, [sites])

	const _renderOptions = (items) => {
		return items.map((item, i) => {
			return <MenuItem value={item.objectId} key={i}>
				<div className={classes.option}>
					{item.name}
				</div>
			</MenuItem>
		})
	}

	const _submitForm = (values) => {
		const kitchenArea = kitchenAreasOptions.find(kitchenArea => kitchenArea.objectId === values.kitchenArea)
		const site = sites.find(site => site.kitchenAreas.some(kitchenArea => kitchenArea.objectId === values.kitchenArea))
		const productionDay = values.productionDay
		
		// Save filters to Redux
		dispatch(updateProductionStepsFilters(site, kitchenArea, productionDay))
		
		// Navigate to prod planner view
		dispatch(showProductionStepsProdPlanner())
	}

	return (
		<div className={classes.wrapper}>
			<div className={classes.container}>
				<Formik
					validationSchema={schema}
					enableReinitialize={true}
					initialValues={{
						kitchenArea: selectedKitchenArea?.objectId,
						productionDay: selectedDate,
					}}
					onSubmit={_submitForm}
				>
					{({
						values,
						setFieldValue,
						handleSubmit,
						handleChange,
					}) => {
						return (
							<Form className={classes.form}>
								<Typography variant="h7" sx={{ fontWeight: 500, fontSize: "16px" }}>
									Sélectionner l'atelier et le jour de production
								</Typography>
								<div className={classes.filtersForm}>
									<FormControl>
										<InputLabel variant="standard" htmlFor="kitchenArea" shrink={!!values.kitchenArea}>Atelier</InputLabel>
										<Select
											variant="standard"
											value={values.kitchenArea}
											name="kitchenArea"
											className={classes.input}
											onChange={(e) => {
												handleChange(e)
												// Update parent state in real-time
												if (setSelectedKitchenArea) {
													const selectedKitchenArea = kitchenAreasOptions.find(kitchenArea => kitchenArea.objectId === e.target.value)
													setSelectedKitchenArea(selectedKitchenArea)
												}
											}}
											disabled={!kitchenAreasOptions.length}
										>
											{_renderOptions(kitchenAreasOptions)}
										</Select>
									</FormControl>
									<FormControl>
										<DatePicker
											disableCloseOnSelect={false}
											showToolbar={false}
											value={values.productionDay}
											name="productionDay"
											variant="inline"
											inputFormat="DD/MM/YYYY"
											required
											onChange={val => {
												const newDate = moment.utc(val).valueOf()
												setFieldValue("productionDay", newDate)
												// Update parent state in real-time
												if (setSelectedDate) {
													setSelectedDate(newDate)
												}
											}}
											renderInput={(params) => <TextField {...params} variant="standard" label="Jour de production" />}
											inputProps={{
												className: classes.dateInput
											}}
										/>
									</FormControl>
								</div>
								<Button
									className={classes.submitButton}
									color="primary"
									type="submit"
									variant="contained"
									onClick={handleSubmit}
								>
									Accéder au plan de production
								</Button>
							</Form>
						)
					}}
				</Formik>
			</div>
		</div>
	)
}

export default ProdPlannerFilter


const useStyles = makeStyles({
	wrapper: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1,
		height: "100%",
	},
	container: {
		flexGrow: 1,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: COLORS.GREY_SUPPLIER_BACKGROUND,
		height: "100%",
	},
	form: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: "24px",
		padding: "24px 32px",
		border: `1px solid ${COLORS.DEFAULT_GREY}`,
		minWidth: "420px"
	},
	title: {
		fontSize: "20px",
		fontWeight: 500,
		color: COLORS.BLACK,
		textAlign: "center",
	},
	filtersForm: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 1fr)",
		gap: "16px 32px",
		width: "100%"
	},
	input: {
		width: "100%"
	},
	dateInput: {
		fontSize: 16,
		width: "100%",
		textAlign: "left"
	},
	submitButton: {
		padding: "11px 16px",
		width: "fit-content"
	},
	option: {
		fontSize: 14
	}
})